import { useEffect, useState } from "react";
import { Loader2, MailWarning, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "../ui";
import { fmtDate, fmtTime } from "./meta";

function LastSent({ last }) {
  if (!last) return <p className="text-xs text-slate-500" data-testid="bsp-notice-last">Not sent yet.</p>;
  return (
    <p className="text-xs text-slate-500" data-testid="bsp-notice-last">
      Last sent {fmtTime(last.at)} ({last.trigger === "schedule" ? "on schedule" : `by ${last.by || "hand"}`}) — {last.removals} removal{last.removals === 1 ? "" : "s"} for {fmtDate(last.removal_date)} · <b>{last.status}</b> · to {last.recipients.join(", ")}
    </p>
  );
}

/** Billing Sync → Settings: the month-end removal heads-up e-mail. */
export function RemovalNoticeCard() {
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const [ask, setAsk] = useState(false);
  const load = () => api.get("/billing-sync/preview/notice").then(({ data }) => { setCfg(data); setTo(data.recipients.join(", ")); });
  useEffect(() => { load().catch((e) => toast.error(errMsg(e, "Could not load the removal e-mail settings"))); }, []);
  if (!cfg) return null;
  const save = async (enabled = cfg.enabled, applyEmail = cfg.apply_email) => {
    setBusy("save");
    try {
      const { data } = await api.put("/billing-sync/preview/notice", { enabled, apply_email: applyEmail, recipients: to.split(/[,;\s]+/).filter(Boolean) });
      setCfg({ ...cfg, ...data }); setTo(data.recipients.join(", ")); toast.success("Removal e-mail saved");
    } catch (e) { load().catch(() => {}); toast.error(errMsg(e, "Could not save")); } finally { setBusy(""); }
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      await api.post("/billing-sync/preview/notice/send");
      toast.success("Running a fresh preview — the e-mail goes out in about two minutes");
      setTimeout(() => load().catch(() => {}), 120000);
    } catch (e) { toast.error(errMsg(e, "Could not send")); } finally { setBusy(""); }
  };
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="bsp-notice-card">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-600"><MailWarning className="h-4 w-4" /></div>
        <div className="flex-1"><h3 className="font-heading text-sm font-semibold text-slate-800">Billing alert e-mails</h3>
          <p className="text-xs text-slate-500"><b>Month-end removals</b> (switch on the right): {cfg.days_before} days before the last day of the month a fresh preview runs and the removals it lists are e-mailed, with every item attached as CSV.</p></div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => { setCfg({ ...cfg, enabled: v }); save(v, cfg.apply_email); }} disabled={!!busy} data-testid="bsp-notice-enabled" />
      </div>
      <div className="space-y-3 px-5 py-4">
        <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
          <span><span className="block text-xs font-medium text-slate-700">E-mail after every apply or undo</span>
            <span className="block text-[11px] text-slate-500">A short summary with the rows that failed.</span></span>
          <Switch checked={cfg.apply_email} onCheckedChange={(v) => { setCfg({ ...cfg, apply_email: v }); save(cfg.enabled, v); }} disabled={!!busy} data-testid="bsp-notice-apply-email" /></label>
        <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Send to</span>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="billingalerts@unitedcloud.ca" className="text-sm" data-testid="bsp-notice-recipients" /></label>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => save()} disabled={!!busy || !to.trim()} data-testid="bsp-notice-save">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
          <Button variant="outline" onClick={() => setAsk(true)} disabled={!!busy || cfg.busy} data-testid="bsp-notice-send">{busy === "send" || cfg.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send it now</Button>
        </div>
        <LastSent last={cfg.last} />
      </div>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="bsp-notice-send-dialog">
          <AlertDialogHeader><AlertDialogTitle>Send the removal e-mail now?</AlertDialogTitle>
            <AlertDialogDescription>A fresh full preview runs first (about a minute and a half), then the removals go to {cfg.recipients.join(", ")}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="bsp-notice-send-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendNow} data-testid="bsp-notice-send-confirm">Send it</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
